import { useEffect, useMemo, useRef, useState } from "react";
import { useConversations } from "../context/ConversationsContext";
import { useAuth } from "../context/AuthContext";
import * as api from "../api/client";
import Composer from "../components/Composer";
import { currentModelSelection } from "../components/ModelPicker";
import AssistantTurn from "../components/AssistantTurn";
import AgentMark from "../components/AgentMark";
import WidgetCard from "../components/WidgetCard";
import AnswerBubble from "../components/AnswerBubble";

// Starters that hold for ANY dataset. The previous list was written for the original bookings
// workbook ("net sales value by project", "5 rows for project Ashford") and stayed hardcoded as the
// app grew to a dozen tables, so most of it named fields that no longer exist anywhere — clicking one
// sent the user a guaranteed dead end. Anything table-specific now comes from `dataStarters` below,
// which is built from the workers the server actually reports.
const GENERIC_EXAMPLES = [
  { t: "See what's available", s: "which tables can I query?", q: "which tables can I query?" },
  { t: "Explore the fields", s: "what fields are available?", q: "what fields are available?" },
];

/** Starters naming real data sources, from the server's own list of data-backed workers.
 *
 * Keyed off `datasets` (see GET /api/agent/agents) rather than filtering the worker list by name:
 * a worker only appears there if it actually has a dataset behind it, so nothing here needs to know
 * that "General" is the conversational one, and the starters follow whatever tables are loaded. */
function dataStarters(datasets) {
  return Object.keys(datasets || {})
    .slice(0, 2)
    .map((name) => ({
      t: name,
      s: "what does this data cover?",
      q: `Using ${name}, what does this data cover and what are its key fields?`,
    }));
}

function emptyLiveTurn() {
  return { status: "Contacting the model", chips: [], blocks: [], done: false };
}

// Pair each restored widget with the assistant message it was rendered under.
//
// A reopened conversation's charts/tables are reconstructed server-side by re-running the original
// query (see api.get_conversation), and each one is tagged with `messageIndex` — the position, in the
// same `messages` array, of the assistant reply it was produced under. That's computed server-side from
// the actual stored tool calls, so it's exact; grouping just has to read it back.
function assignWidgets(messages, widgets) {
  const byIndex = new Map();
  const leftover = [];
  for (const w of widgets) {
    const idx = w.messageIndex;
    if (typeof idx !== "number" || idx < 0 || idx >= messages.length) {
      leftover.push(w);
      continue;
    }
    if (!byIndex.has(idx)) byIndex.set(idx, []);
    byIndex.get(idx).push(w);
  }
  return { byIndex, leftover };
}

// One message row, with any widgets it produced folded into the SAME card (`turn-holder`) rather than
// rendered as a separate element below — otherwise a reopened conversation shows a plain text bubble
// with a visually detached chart/table floating under it, instead of the one unified reply card a live
// turn renders as (see AssistantTurn). `widgets` is only ever non-empty for an assistant message.
function renderMessage(m, i, widgets) {
  if (m.role === "user") {
    // No avatar on the user's own messages — the right alignment already says who wrote it.
    return (
      <div className="row user" key={i}>
        <div className="bubble">{m.content}</div>
      </div>
    );
  }
  if (m.rich) return <AssistantTurn key={i} turn={{ ...m.rich, status: null, done: true }} />;
  return (
    <div className="row assistant" key={i}>
      <AgentMark />
      <div className="turn-holder">
        <AnswerBubble markdown={m.content} />
        {widgets.map((w) => (
          <WidgetCard key={w.id} kind={w.kind} spec={w.spec} />
        ))}
      </div>
    </div>
  );
}

// Role shown in the chat header: admin status wins (it's the more useful fact when both are set),
// then the assigned role name, then nothing — an unassigned role has nothing worth badging.
function roleLabel(user) {
  if (!user) return null;
  if (user.isAdmin) return "Admin";
  return user.role || null;
}

// The active conversation's title, editable in place. Renaming is only possible once the conversation
// actually has a row server-side (i.e. after its first message) — `touch()` only inserts one then, so
// a brand-new empty chat has nothing yet to rename and falls back to a static "New chat" label.
function ChatTitle({ activeId, conversations, renameConversationLocal }) {
  const conv = conversations.find((c) => c.id === activeId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = () => {
    if (!conv) return;
    setDraft(conv.title || "");
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    const title = draft.trim();
    if (!conv || !title || title === conv.title) return;
    try {
      await api.renameConversation(conv.id, title);
      renameConversationLocal(conv.id, title);
    } catch {
      // best-effort — the header just keeps showing the old title on failure
    }
  };

  if (editing) {
    return (
      <input
        className="chat-title-input"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }
  return (
    <h1
      className={conv ? "chat-title-editable" : undefined}
      title={conv ? "Click to rename" : undefined}
      onClick={startEdit}
    >
      {conv?.title || "Data Assistant"}
    </h1>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const { activeId, setActiveId, conversations, renameConversationLocal, refresh: refreshConversations } =
    useConversations();
  const [messages, setMessages] = useState([]);
  const [historyWidgets, setHistoryWidgets] = useState([]);
  // The data sources this server actually exposes, so the heading and starters describe THIS install
  // rather than the one dataset the app happened to ship with. Failure is non-fatal: the generic
  // starters alone still work, and an empty roster just means no data worker is registered.
  const [datasets, setDatasets] = useState({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveTurn, setLiveTurn] = useState(null);
  const chatRef = useRef(null);
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  const abortRef = useRef(null);

  // Load a conversation's persisted text + any charts/tables it previously produced — the server
  // reconstructs those fresh from the stored tool calls (see api.get_conversation), not from a saved
  // copy. Widgets fetched here are historical only — turns sent *this* session render their own
  // chart/table blocks live and are not duplicated from this fetch.
  useEffect(() => {
    let cancelled = false;
    setLiveTurn(null);
    if (!activeId) {
      setMessages([]);
      setHistoryWidgets([]);
      return;
    }
    (async () => {
      try {
        const conv = await api.getConversation(activeId);
        if (cancelled) return;
        setMessages(conv.messages || []);
        setHistoryWidgets(conv.widgets || []);
      } catch {
        if (!cancelled) {
          setMessages([]);
          setHistoryWidgets([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // Which restored widget belongs under which message. Memoised because it runs on every render and
  // the inputs only change when a conversation is loaded or a turn completes.
  const widgetPlan = useMemo(() => assignWidgets(messages, historyWidgets), [messages, historyWidgets]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, liveTurn, historyWidgets]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    let cancelled = false;
    api
      .listAgents()
      .then((r) => !cancelled && setDatasets(r.datasets || {}))
      .catch(() => {}); // starters fall back to the generic pair
    return () => {
      cancelled = true;
    };
  }, []);

  const examples = useMemo(() => [...dataStarters(datasets), ...GENERIC_EXAMPLES], [datasets]);
  const sourceCount = Object.keys(datasets).length;

  async function send(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;

    const id = activeId || crypto.randomUUID();
    if (!activeId) setActiveId(id);

    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);

    const draft = emptyLiveTurn();
    setLiveTurn({ ...draft });
    let speaker = null;
    let worker = null;  // most recent specialist the supervisor handed to, for status text

    const controller = new AbortController();
    abortRef.current = controller;

    const publish = () => setLiveTurn({ ...draft, chips: [...draft.chips], blocks: [...draft.blocks] });

    try {
      const { model, apiKey } = currentModelSelection();
      for await (const { event, data } of api.streamChat(id, trimmed, {
        signal: controller.signal,
        model: model || undefined,
        apiKey: apiKey || undefined,
      })) {
        switch (event) {
          case "status":
            draft.status = "Choosing a specialist";
            break;
          case "agent":
            if (data.state === "delegated") {
              draft.chips.push({ kind: "handoff", id: `h-${draft.chips.length}`, agent: data.agent });
              worker = data.agent;
              draft.status = `${data.agent} is working`;
            } else if (data.state === "answering") {
              speaker = data.agent;
              draft.status = `${data.agent} is writing the answer`;
            }
            break;
          case "tool": {
            const i = draft.chips.findIndex((c) => c.kind === "tool" && c.id === data.id);
            const chip = { kind: "tool", ...data };
            if (i >= 0) draft.chips[i] = { ...draft.chips[i], ...chip };
            else draft.chips.push(chip);
            // Name the query in flight. A tool call can sit for many seconds against a hosted model,
            // and "Data is working" for 20s reads as stalled where "Running query_data" does not.
            if (data.status === "running") draft.status = `Running ${data.tool}`;
            else if (data.status === "ok") draft.status = `${worker || "The assistant"} is working`;
            break;
          }
          case "chart":
            draft.blocks.push({ type: "chart", spec: data });
            break;
          case "kpi":
            draft.blocks.push({ type: "kpi", spec: data });
            break;
          case "table":
            draft.blocks.push({ type: "table", spec: data });
            break;
          case "error":
            draft.blocks.push({ type: "error", message: `${data.message} (${data.code})` });
            draft.status = null;
            break;
          case "done":
            draft.status = null;
            draft.done = true;
            break;
          case "token": {
            const last = draft.blocks[draft.blocks.length - 1];
            if (last && last.type === "text" && last.speaker === speaker) last.text += data;
            else draft.blocks.push({ type: "text", speaker, text: data });
            break;
          }
          default:
            break;
        }
        publish();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        draft.status = null;
        draft.blocks.push({ type: "error", message: "Could not reach the server: " + err.message });
        publish();
      }
    } finally {
      // If the user switched to a different chat while this turn was in flight, don't splice its
      // answer into whatever conversation is now on screen. The turn still completed and persisted
      // server-side — reopening that chat will show its final text.
      if (activeIdRef.current === id) {
        setMessages((prev) => [...prev, { role: "assistant", rich: { chips: draft.chips, blocks: draft.blocks } }]);
        setLiveTurn(null);
      }
      setBusy(false);
      refreshConversations();
    }
  }

  const showEmpty = !activeId && messages.length === 0 && !liveTurn;

  return (
    <main className="main">
      <div className="topbar">
        <div className="topbar-row">
          <ChatTitle
            activeId={activeId}
            conversations={conversations}
            renameConversationLocal={renameConversationLocal}
          />
          {roleLabel(user) && <span className="role-badge">{roleLabel(user)}</span>}
        </div>
        <p>
          Ask about your data, look up records, or request a chart.
          {sourceCount > 0 && ` ${sourceCount} data source${sourceCount === 1 ? "" : "s"} connected.`}
        </p>
      </div>

      <div id="chat" ref={chatRef}>
        {showEmpty ? (
          <div className="empty">
            <div className="hi">How can I help with your data?</div>
            <div className="sub">Query records, summarize, or ask for a chart — all answered from the dataset.</div>
            <div className="examples">
              {examples.map((ex) => (
                <button key={ex.t} className="example" onClick={() => send(ex.q)}>
                  <b>{ex.t}</b>
                  <span>{ex.s}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Each restored chart/table renders under the turn that produced it (see assignWidgets),
                so a reopened conversation reads the same way it did live. Collecting them into one
                block instead put every chart in the conversation at the very bottom, under whatever
                had just been asked. */}
            {messages.map((m, i) => renderMessage(m, i, widgetPlan.byIndex.get(i) || []))}

            {/* Only what could not be matched to a turn — widgets saved before narration was stored.
                Better shown together at the end than dropped. */}
            {widgetPlan.leftover.length > 0 && (
              <div className="history-widgets">
                <div className="who">Charts &amp; tables from earlier in this conversation</div>
                {widgetPlan.leftover.map((w) => (
                  <WidgetCard key={w.id} kind={w.kind} spec={w.spec} />
                ))}
              </div>
            )}

            {liveTurn && <AssistantTurn turn={liveTurn} />}
          </>
        )}
      </div>

      <Composer value={input} onChange={setInput} onSend={() => send()} disabled={busy} />
    </main>
  );
}
